/**
 * SEO Configuration
 *
 * Page-specific metadata for all 8 public pages.
 * Titles: 50-60 characters
 * Descriptions: 150-160 characters
 */

import type { PageSEOMetadata } from '../types/seo'

export const seoConfig: Record<string, PageSEOMetadata> = {
  '/': {
    path: '/',
    title: 'National Council for Equal Shared Parenting | NCESP',
    description:
      "Advocating for children's rights to maintain healthy relationships with both parents. Join NCESP in supporting equal shared parenting and family court reform.",
    keywords: [
      'equal shared parenting advocacy',
      'NCESP',
      "children's rights family court",
      'parental rights',
      'custody reform',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/about': {
    path: '/about',
    title: 'About NCESP | Equal Shared Parenting Advocacy',
    description:
      'Learn about the National Council for Equal Shared Parenting, our 40-year expertise in family law reform, and our mission to ensure children maintain healthy relationships with both parents.',
    keywords: [
      'NCESP',
      'equal shared parenting',
      'family court reform',
      'nonprofit organization',
      'parental rights advocacy',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/leadership': {
    path: '/leadership',
    title: 'Leadership Team | NCESP Officers & Board',
    description:
      'Meet the experienced leadership team at NCESP, including Founder Mark Ludwig with 40 years of political expertise in family law reform and equal parenting advocacy.',
    keywords: [
      'NCESP leadership',
      'Mark Ludwig',
      'equal parenting advocates',
      'family court reform leaders',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/issues': {
    path: '/issues',
    title: 'Key Issues | Family Court Reform & Parental Rights',
    description:
      'Explore critical issues in family court reform including equal shared parenting, child support enforcement, and parental rights. Learn how NCESP advocates for children and families.',
    keywords: [
      'family court reform',
      'parental rights',
      'custody issues',
      'child support reform',
      'equal parenting',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/issues/child-support-reform': {
    path: '/issues/child-support-reform',
    title: 'Child Support Reform | Title IV-D System Reform',
    description:
      "Understanding Title IV-D child support enforcement reform. Learn how NCESP advocates for fair, balanced child support systems that prioritize children's wellbeing and family stability.",
    keywords: [
      'title IV-D reform',
      'child support enforcement reform',
      'family court reform',
      'child support fairness',
      'parental support',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/issues/equal-shared-parenting': {
    path: '/issues/equal-shared-parenting',
    title: 'Equal Shared Parenting | 50/50 Custody Advocacy',
    description:
      'Advocating for equal shared parenting presumption and 50/50 custody laws. Research shows children thrive with equal time with both fit parents after divorce or separation.',
    keywords: [
      '50/50 custody laws',
      'shared parenting presumption',
      'equal custody rights',
      "children's wellbeing",
      'parenting time',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/contact': {
    path: '/contact',
    title: 'Contact NCESP | Get Involved in Equal Parenting',
    description:
      "Contact the National Council for Equal Shared Parenting. Subscribe for updates, ask questions, or learn how to support children's rights to both parents. Join our community today.",
    keywords: [
      'contact NCESP',
      'equal parenting support',
      'join advocacy',
      'family court help',
      'parental rights contact',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/events': {
    path: '/events',
    title: 'Events & Calendar | NCESP Meetings & Advocacy',
    description:
      'View upcoming NCESP events, meetings, webinars, and advocacy actions. Browse our calendar to find opportunities to support equal shared parenting.',
    keywords: [
      'NCESP events',
      'equal shared parenting events',
      'family court reform meetings',
      'advocacy calendar',
      'NCESP meetings',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/donate': {
    path: '/donate',
    title: 'Donate - Support Equal Shared Parenting | NCESP',
    description:
      'Support the National Council for Equal Shared Parenting. Your contribution helps us advocate for legislative change, raise public awareness, and provide resources to families.',
    keywords: [
      'donate to NCESP',
      'support equal parenting',
      'family advocacy donation',
      'nonprofit donation',
      'parental rights funding',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/privacy-policy': {
    path: '/privacy-policy',
    title: 'Privacy Policy | NCESP Data Protection',
    description:
      'NCESP Privacy Policy: Learn how we collect, use, and protect your personal information. We are committed to data minimalism and transparency in all our practices.',
    keywords: ['privacy policy', 'data protection', 'NCESP privacy'],
    ogImage: '/images/og/default.jpg',
    noIndex: true, // Exclude from search results but preserve link equity
  },

  '/research': {
    path: '/research',
    title: 'Research & Evidence | NCESP',
    description:
      'Evidence-based research summaries on shared parenting, overnights, and child outcomes from the National Council for Equal Shared Parenting.',
    keywords: [
      'shared parenting research',
      'equal custody research',
      'child outcomes',
      'parenting plans evidence',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },

  '/research/overnights-and-parenting-plans': {
    path: '/research/overnights-and-parenting-plans',
    title: 'Overnights & Parenting Plans Research | NCESP',
    description:
      'What does the research say about overnights for young children in shared parenting plans? Evidence-based summary of peer-reviewed studies.',
    keywords: [
      'overnights young children',
      'parenting plans research',
      'shared custody overnights',
      'Warshak overnight study',
    ],
    ogImage: '/images/og/default.jpg',
    noIndex: false,
  },
}

/**
 * Get SEO configuration for a specific page path
 */
export function getSEOConfigByPath(path: string): PageSEOMetadata | undefined {
  return seoConfig[path]
}
